import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { callApi } from 'src/utils/apiCall';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { TEMPLATE_STATUS_OPTIONS } from 'src/constants/tasksV2';


// libraries
import { useForm, Controller } from 'react-hook-form';
import { useDebounce } from 'use-lodash-debounce';
import {
    Box,
    Input,
    Portal,
    Button,
    Backdrop,
    Divider,
    IconButton,
    Typography,
    FormControlLabel,
    OutlinedInput,
    Switch,
    Select,
    MenuItem,
    InputLabel,
    InputAdornment,
    FormControl,
    CircularProgress,
    Autocomplete,
    TextField,
    Dialog,
    DialogTitle,
    DialogActions,
    Popover,
    InputBase,
    Tooltip,
    styled,
    Chip,
    Collapse,
    Radio,
    RadioGroup,
    FormLabel,
    Checkbox,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { isEmpty } from 'lodash';
// components
import { APPLE } from 'src/components/templates/appleDesignTokens';
import { setCurrentTreatmentDataGlobal } from 'src/redux/slices/multiTreatment';
import SendMessageDialog from './SendMessageDialog';
import Editor from '../../../components/editor/Editor';
import { Iconify } from '../../../components';
import { RHFTextField, FormProvider, RHFAutocomplete } from '../../../components/hook-form';
import { SearchNotFound } from '../../../components/search-not-found';
import { OpenDialogPopup } from '../../../components/fullScreenPopUps';
import { AttendedAppointment } from '../../../components/Attended';
import { PersonalDetails, TreatmentAndAppointment, PaymentOption, MessageAndReminder } from '../general/app';
import CustomMediaLibrary from './CustomMediaLibrary';
// Store
import { getMediaImages, setBasicBody, setFileUploadError } from '../../../redux/slices/campaign';
import { useSelector, dispatch } from '../../../redux/store';
import { clearTaskLead, searchLead, createNewLead, clearLead, saveConsentLead } from '../../../redux/slices/lead';
import { updateError } from '../../../redux/slices/user';
import { updateShowSubjectList, uploadImage } from '../../../redux/slices/practiceMessage';
// hooks/constants/util
import { useResponsive, useToggle, useConsentToggle, useBoolean } from '../../../hooks';
import useEmailAttachments, { getEmailAttachmentSendMode } from '../../../hooks/useEmailAttachments';
import { addCategory, BeforeComa, AfterComa } from '../../../constants/EmailMessageComposeConst';
import { applyStylesToTags } from '../../../utils/applyStylesToTags ';
import { getDelayLabel } from '../../../utils/formatTime';
import { formatMergeTagToken } from '../../../utils/mergeTagToken';
// style
import { cancelBtnStyle, yesBtnStyle } from '../../../styles/NewLead';
import {
    backdropStyle,
    rootStyle,
    imgLoaderBoxStyle,
    formControlStyle,
    multipleChipStyle,
    editorBoxStyle,
    editorBoxBtnStyle,
    RHFTextFieldStyle,
    saveBtnStyle,
    selectMenuItemStyle,
    typographyStyle,
    commaSpanStyle,
    subjectTypoTextStyle,
    subjectInputBoxStyle,
    subjectInputStyle,
} from '../../../styles/EmailMessageComposeStyle';

const RootStyle = styled('div')(({ theme }) => ({
    right: 0,
    bottom: 0,
    zIndex: 1119,
    minHeight: 440,
    outline: 'none',
    display: 'flex',
    position: 'fixed',
    overflow: 'hidden',
    flexDirection: 'column',
    margin: theme.spacing(3),
    boxShadow: theme.customShadows.z20,
    borderRadius: Number(theme.shape.borderRadius) * 2,
    backgroundColor: theme.palette.background.paper,
}));

const InputStyle = styled(Input)(({ theme }) => ({
    padding: theme.spacing(0.5, 3),
    borderBottom: `solid 1px ${theme.palette.divider}`,
    ' input:focus::placeholder': {
        color: 'transparent'
    }
}));

const PRIORITY_TAGS = [
    'lead_first_name', 'lead_last_name', 'lead_email', 'lead_phone',
    'practice_name', 'practice_phone', 'practice_email',
    'practice_full_address', 'booking_url', 'treatment_name'
];

const EmailMessageCompose = ({
    openEmailCompose,
    onCloseEmailCompose,
    composeDetail,
    isEdit,
    type,
    handleSaveMessage,
    messageType,
    practiceId,
    hasTargeting: hasTargetingProp,
    OpenTemplate,
    useOrSend,
    setTemplate,
    setTemplateOpen,
    handleSendEmail,
    leadDetail,
    sendTemplate,
    screen,
    campaign,
    isFromSearch,
    preventDefaultBaseTemplateAndID
}) => {

    const defaultValues = {
        leadName: ''
    };

    const methods = useForm({
        defaultValues
    });
    const EditorMessage = useRef();
    const inputRef = useRef();
    const isDesktop = useResponsive('up', 'sm');

    // eslint-disable-next-line no-nested-ternary
    const initialIsUnLayer = campaign ? !isEmpty(composeDetail) ? composeDetail?.use_unlayer : true : false;

    const [body, setBody] = useState(composeDetail?.body || '');
    const [unLayerHtml, setUnLayerHtml] = useState(composeDetail?.unlayer_html || '');
    const [templateJson, setTemplateJson] = useState(composeDetail?.unlayer_template || {});
    const [fullScreen, setFullScreen] = useState(false);
    const [editForm, setEditForm] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [user, setUser] = useState({});
    const [validStep, setValidStep] = useState(false);
    const [active, setActive] = useState(0);
    const [isUnlayer, setIsUnlayer] = useState(initialIsUnLayer);
    const [customMergeTags, setCustomMergeTags] = useState({});
    const [inputFiles, setInputFiles] = useState(null);
    const [cursor, setCursor] = useState(0);
    const attachmentSaveInFlightRef = useRef(false);
    const [attachmentSaveInFlight, setAttachmentSaveInFlight] = useState(false);
    const { imageLoader } = useSelector((state) => state.practiceMessage);
    const { mediaImages, basicBody } = useSelector((state) => state.campaign);
    const { createLeadForConsent } = useSelector((state) => state.lead);
    // eslint-disable-next-line no-unused-vars
    const [practiceUserData, setPracticeUserData] = useState([]);

    const {
        acknowledgeSaved,
        createSaveSnapshot,
        dirty: attachmentDirty,
        error: attachmentError,
        hasBlockingError: attachmentBlocked,
        newFiles: attachmentFiles,
        pending: attachmentPending,
        prepareForSend,
        removeSavedAttachment,
        replaceNewFiles,
        savedAttachments,
        templatePayload: attachmentPayload,
        validationError: attachmentValidationError,
    } = useEmailAttachments({
        resetKey: `${openEmailCompose ? 'open' : 'closed'}:${composeDetail?.id || 'new'}`,
        savedAttachments: composeDetail?.attachments || [],
    });

    const { mergeTags: mergeTagsInfo } = useSelector(state => ({ mergeTags: state.mergeTags }));

    const mergeTags = mergeTagsInfo?.mergeTagKeys;

    // Targeting feature — "When should this template be available?"
    // Only shown/applied when messageType === 'Template' and feature flag is on.
    // `hasTargetingProp` lets a host that already resolved the flag (the template
    // grid, whose rows span practices) override the per-practice check.
    const hasTargeting = hasTargetingProp ?? (practiceId ? hasFeatureAccess('engagement-template-targeting', practiceId) : false);
    const showTargetingSection = hasTargeting && messageType === 'Template';

    const [targetingExpanded, setTargetingExpanded] = useState(false);
    const [statusMode, setStatusMode] = useState('any');
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [categoryMode, setCategoryMode] = useState('any');
    const [selectedTreatmentCategories, setSelectedTreatmentCategories] = useState([]);
    const [itemMode, setItemMode] = useState('any');
    const [selectedTreatmentItems, setSelectedTreatmentItems] = useState([]);
    const [treatmentsData, setTreatmentsData] = useState({ categories: [], items: [] });

    // Branded Mailgun template wiring — engagement templates only.
    const showTemplateWiring = messageType === 'Template';
    const [mgWrapped, setMgWrapped] = useState(!!composeDetail?.use_mailgun_templates);
    const [mgTemplateId, setMgTemplateId] = useState(composeDetail?.template_id || '');
    const [mgOptions, setMgOptions] = useState([]);
    const [mgLoading, setMgLoading] = useState(false);
    const [mgWiringExpanded, setMgWiringExpanded] = useState(false);

    useEffect(() => {
        setMgWrapped(!!composeDetail?.use_mailgun_templates);
        setMgTemplateId(composeDetail?.template_id || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [composeDetail?.id]);

    useEffect(() => {
        if (!showTemplateWiring || !practiceId) return;
        let cancelled = false;
        setMgLoading(true);
        callApi(`/api/v1/practices/${practiceId}/engagement-templates/mailgun-templates/`)
            .then((res) => {
                if (cancelled) return;
                const list = res?.ok ? (res.data?.templates || []) : [];
                setMgOptions(list.map((t) => t.name).filter(Boolean));
            })
            .catch(() => { if (!cancelled) setMgOptions([]); })
            .finally(() => { if (!cancelled) setMgLoading(false); });
        // eslint-disable-next-line consistent-return
        return () => { cancelled = true; };
    }, [showTemplateWiring, practiceId]);

    const textareaRef = useRef(null);
    const {
        taskSearchLead,
        isLoading,
        attendance,
        markAsComplete,
        followUpLead,
    } = useSelector((state) => state.lead);

    const getNameList = () => mergeTags?.map(dt => (mergeTagsInfo?.mtValPractice?.[dt] ?? mergeTagsInfo?.mtValUser?.[dt] ? `$${dt} (${mergeTagsInfo?.mtValPractice?.[dt] ?? mergeTagsInfo?.mtValUser?.[dt]})` : `$${dt}`))

    const formatTagName = (tag) => tag.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    const sortedMergeTags = React.useMemo(() => {
        if (!mergeTags || mergeTags.length === 0) return [];
        const priority = PRIORITY_TAGS.filter(t => mergeTags.includes(t));
        const rest = mergeTags.filter(t => !PRIORITY_TAGS.includes(t));
        return [...priority, ...rest];
    }, [mergeTags]);

    const [moreTagsAnchor, setMoreTagsAnchor] = useState(null);
    const [tagSearch, setTagSearch] = useState('');

    const filteredAllTags = React.useMemo(() => {
        if (!sortedMergeTags.length) return [];
        if (!tagSearch.trim()) return sortedMergeTags;
        const q = tagSearch.toLowerCase();
        return sortedMergeTags.filter(t => formatTagName(t).toLowerCase().includes(q));
    }, [sortedMergeTags, tagSearch]);

    const handleMergeTagClick = (tagKey) => {
        if (isUnlayer) {
            const currentSubject = compose.subject || '';
            handleChangeEvent(`${currentSubject}${formatMergeTagToken(tagKey)}`, 'subject');
        } else {
            handleChange(null, formatMergeTagToken(tagKey));
        }
    };

    const editor = document.getElementsByClassName('.email-message-compose ql-editor') && document.getElementsByClassName('.email-message-compose ql-editor')[0] && document.getElementsByClassName('.email-message-compose ql-editor')[0]?.innerHTML;

    const debouncedValue = useDebounce(searchQuery, 1000);
    const autoSaveFunction = useDebounce(unLayerHtml, 5000);
    const [isAutoSaveOn, setIsAutoSaveOn] = useState(false);

    const templateConfirmation = useBoolean();


    const {
        toggle: openContacts,
        onOpen: onOpenContacts,
        onClose: onCloseContacts
    } = useToggle();
    const {
        toggle: openCustomLib,
        onOpen: onOpenCustomLib,
        onClose: onCloseCustomLib
    } = useToggle();

    const {
        toggle: ConsentBuilder,
        onOpen: onOpenConsent,
        onClose: onCloseConsent
    } = useConsentToggle();

    const {
        control,
        setValue,
        clearErrors,
        getValues,
    } = methods;

    const onSubmit = (data) => {
        setValidStep(!!data);
    };


    const [compose, setCompose] = useState({
        subject: composeDetail?.subject || '',
        is_enabled: composeDetail?.is_enabled,
        delay: composeDetail?.delay && { label: getDelayLabel(composeDetail.delay, composeDetail?.delay_human), second: composeDetail.delay } || { label: 'Immediately', second: 0 },
        personName: composeDetail?.personName || [],
        category: composeDetail?.category || '',
        channel: composeDetail?.channel || 'EMAIL',
        name: composeDetail?.name || ''
    });

    // useEffect(() => {
    //     if (practiceId) {
    //         dispatch(getMediaImages({ practiceId }));
    //     }
    // }, [practiceId]);


    useEffect(() => {
        const fetchData = async () => {
            if (practiceId) {
                dispatch(getMediaImages({ practiceId }));

                try {
                    const respContextPractice = await callApi(
                        `/api/v1/practices/${practiceId}/get_context/`,
                        'GET'
                    );
                    setPracticeUserData(() => respContextPractice)

                } catch (error) {
                    console.error('Error fetching context practice:', error);
                }
            }
        };

        fetchData();
    }, [practiceId, dispatch]);



    useEffect(() => {
        if (!isEmpty(basicBody)) {
            setBody(basicBody);
        }
    }, [basicBody]);

    useEffect(() => {
        setUser(createLeadForConsent);
        setValue('lead_id', createLeadForConsent?.id);
        setSearchQuery(`${createLeadForConsent?.first_name || ''} ${createLeadForConsent?.last_name || ''}`);
    }, [createLeadForConsent]);

    useEffect(() => {
        if (Object.keys(composeDetail).length > 0) {
            if (Object.keys(composeDetail).length > 0) {
                setCompose({
                    subject: composeDetail.subject || '',
                    is_enabled: composeDetail.is_enabled,
                    delay: composeDetail.delay && { label: getDelayLabel(composeDetail.delay, composeDetail.delay_human), second: composeDetail.delay } || { label: 'Immediately', second: 0 },
                    personName: composeDetail.personName || [],
                    category: composeDetail.category || '',
                    channel: composeDetail.channel || 'EMAIL',
                    name: composeDetail.name || ''
                });
                setBody(composeDetail.body || '');
                setUnLayerHtml(composeDetail.unlayer_html || '');
                // Targeting fields — only populate when feature is on
                if (showTargetingSection) {
                    const statuses = composeDetail.applies_to_statuses || [];
                    setSelectedStatuses(statuses);
                    setStatusMode(statuses.length > 0 ? 'specific' : 'any');

                    const cats = composeDetail.applies_to_treatment_categories || [];
                    setSelectedTreatmentCategories(cats);
                    setCategoryMode(cats.length > 0 ? 'specific' : 'any');

                    const items = composeDetail.applies_to_treatment_items || [];
                    setSelectedTreatmentItems(items);
                    setItemMode(items.length > 0 ? 'specific' : 'any');

                    if (statuses.length > 0 || cats.length > 0 || items.length > 0) {
                        setTargetingExpanded(true);
                    }
                }
            }
        }
    }, [composeDetail]);

    useEffect(() => {
        if (markAsComplete && !followUpLead) {
            dispatch(createNewLead(onCloseCreateLead));
        }
    }, [markAsComplete, followUpLead]);

    // Load treatment list for the targeting section (only when feature is on and messageType=Template)
    useEffect(() => {
        if (!showTargetingSection || !practiceId) return;
        // Keep enabled=true here — the picker should only offer enabled treatments
        // as targets. Limit raised from 200→500 to reduce truncation for large practices.
        callApi(`/api/v1/practices/${practiceId}/treatments/?fields={enabled,id,treatment_item{id,name,treatment_category{id,name}}}&ordering=treatment_item__name&enabled=true&limit=500`).then((res) => {
            if (!res.ok) return;
            const results = res.data?.results || [];
            const seenCats = new Set();
            const seenItems = new Set();
            const cats = [];
            const items = [];
            results.forEach((t) => {
                const catId = t.treatment_item?.treatment_category?.id;
                const catName = t.treatment_item?.treatment_category?.name;
                const itemId = t.treatment_item?.id;
                const itemName = t.treatment_item?.name;
                if (catId && !seenCats.has(catId)) {
                    seenCats.add(catId);
                    cats.push({ id: catId, name: catName || 'Uncategorised' });
                }
                if (itemId && !seenItems.has(itemId)) {
                    seenItems.add(itemId);
                    items.push({ id: itemId, name: itemName || 'Unknown', catName: catName || '' });
                }
            });
            cats.sort((a, b) => a.name.localeCompare(b.name));
            items.sort((a, b) => a.name.localeCompare(b.name));
            setTreatmentsData({ categories: cats, items });
        });
    }, [showTargetingSection, practiceId]);

    useEffect(() => {
        if (searchQuery.trim().length > 0) {
            let search = searchQuery;
            if (Number(searchQuery) && !searchQuery.includes('+')) {
                search = `+44${searchQuery}`;
            }
            dispatch(searchLead(encodeURIComponent(search), practiceId, 'taskLead'));
        }
    }, [debouncedValue]);

    useEffect(() => {
        if (sendTemplate && sendTemplate) {
            setEditForm(true);
        }
    }, [sendTemplate]);

    useEffect(() => {
        if (editor && (editor !== composeDetail.body)) {
            setEditForm(true);
        }
    }, [editor, composeDetail]);

    const [dataMap, setDataMap] = React.useState({});

    React.useEffect(() => {
        setDataMap(prv => ({ ...prv, ...(mergeTagsInfo?.mtValPractice ?? {}), ...(mergeTagsInfo?.mtValUser ?? {}) }))
        if (!mergeTagsInfo?.mtValLoadingLeads && !mergeTagsInfo?.mtValLoadingTreatments) {
            setDataMap(prv => ({ ...prv, ...(mergeTagsInfo?.mtValTreatments ?? {}), ...(mergeTagsInfo?.mtValLeads ?? {}) }))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mergeTagsInfo?.mtValLoadingLeads, mergeTagsInfo?.mtValLoadingTreatments]);

    const STEPS = [
        {
            label: 'Personal details',
            component: <PersonalDetails ref={inputRef} handleNext={onSubmit} active={active} />
        },
        {
            label: 'Treatment & Appointment',
            component: <TreatmentAndAppointment type={'CreatLead'} ref={inputRef} handleNext={onSubmit} active={active} />
        }
    ];

    const [leadStep, setLeadStep] = useState(STEPS);

    useEffect(() => {
        if (attendance) {
            const steps = [...STEPS];
            if (attendance === 'attendance') {
                steps.push({
                    label: 'Appointment Outcome',
                    component: <AttendedAppointment ref={inputRef} handleNext={onSubmit} active={active} type={'CreatedLead'} />
                });
            } else {
                steps.push({
                    label: 'Payment options',
                    component: <PaymentOption ref={inputRef} handleNext={onSubmit} active={active} />
                });
                steps.push({
                    label: 'Message review & reminders',
                    component: <MessageAndReminder ref={inputRef} handleNext={onSubmit} active={active} />
                });
            }
            setLeadStep(steps);
        } else {
            setLeadStep(STEPS);
        }
    }, [attendance]);

    const handleClose = () => {
        templateConfirmation.onFalse();
        onCloseEmailCompose();
        setFullScreen(false);
        if (screen !== 'consent') {
            setCompose({
                subject: '',
                is_enabled: composeDetail.is_enabled,
                delay: composeDetail.delay && { label: getDelayLabel(composeDetail.delay, composeDetail.delay_human), second: composeDetail.delay } || { label: 'Immediately', second: 0 },
                personName: composeDetail.personName || [],
                category: '',
                channel: 'EMAIL',
                name: ''
            });
            setBody('');
            dispatch(setBasicBody(''));
            setUnLayerHtml('');
        }
    };

    const onCloseCreateLead = () => {
        onCloseConsent();
        dispatch(clearLead());
        dispatch(setCurrentTreatmentDataGlobal(null));
    };

    useEffect(() => {
        if (mergeTags) {
            const modifiedData = {};
            // eslint-disable-next-line no-restricted-syntax
            for (const mergeTag of mergeTags) {
                modifiedData[mergeTag] = {
                    name: mergeTag,
                    sample: mergeTag,
                    value: formatMergeTagToken(mergeTag),
                };
            }
            setCustomMergeTags(modifiedData);
        }
    }, [mergeTags]);

    const isLoaded = useRef(true);

    const handleChangeEvent = (value, type) => {
        setEditForm(true);
        setIsAutoSaveOn(true);
        if (campaign && isUnlayer) {
            EditorMessage?.current?.editor?.setMergeTags(customMergeTags);
            EditorMessage?.current?.editor?.registerCallback('image', (file, done) => {
                const reader = new FileReader();
                reader.readAsDataURL(file?.accepted[0]);
                reader.onload = () => {
                    const detail = {
                        image: reader.result
                    };
                    dispatch(uploadImage(detail, (data) => {
                        if (data.success) {
                            const { image } = data.data;
                            if (image) {
                                done({ progress: 100, url: image });
                            }
                            return;
                        }
                        dispatch(updateError({ success: false, message: data.message.image.toString() }));
                    }));
                };
            });
            EditorMessage?.current?.editor?.exportHtml((data) => {
                const { html, design } = data;
                setUnLayerHtml(html);
                setTemplateJson(design);
            }, {
                minify: true,
            });
            if (type === 'subject' || type === 'name' || type === 'category') {
                setCompose({
                    ...compose,
                    [type]: value,
                });
            }
            return;
        }
        if (type === 'body') {
            setBody(value);
            return;
        }
        setCompose({
            ...compose,
            [type]: value,
        });
    };

    const handleSave = (isSaveAndClose) => {
        if (attachmentSaveInFlightRef.current) return;
        if (attachmentPending || attachmentBlocked) {
            dispatch(updateError({
                success: false,
                message: attachmentValidationError?.message || attachmentError?.message || 'Please wait for attachments to finish processing.',
            }));
            return;
        }
        if (!isUnlayer && body === '<p><br></p>') {
            dispatch(updateError({ success: false, message: 'Template should be not empty' }));
            return;
        }
        const content = editor || body || '';
        const modifiedContent = applyStylesToTags(content);
        if (messageType === 'Practice_Message' || messageType === 'OnBoarding') {
            const detail = {
                delay: compose.delay.second || 0,
                subject: compose.subject || '',
                use_unlayer: isUnlayer,
                is_enabled: compose.is_enabled,
            };
            if (campaign && isUnlayer) {
                detail.unlayer_html = unLayerHtml || '';
                detail.unlayer_template = templateJson || {};
            } else {
                detail.body = modifiedContent;
            }
            handleSaveMessage(detail, '', isSaveAndClose);
            return;
        }
        // Targeting validation — block save if "Specific" is selected with no values chosen.
        // Only runs for the Template path (Practice_Message/OnBoarding already returned above).
        if (showTargetingSection) {
            if (statusMode === 'specific' && selectedStatuses.length === 0) {
                dispatch(updateError({ success: false, message: 'Select at least one lead status, or switch back to "Any"' }));
                setTargetingExpanded(true);
                return;
            }
            if (categoryMode === 'specific' && selectedTreatmentCategories.length === 0) {
                dispatch(updateError({ success: false, message: 'Select at least one treatment category, or switch back to "Any"' }));
                setTargetingExpanded(true);
                return;
            }
            if (itemMode === 'specific' && selectedTreatmentItems.length === 0) {
                dispatch(updateError({ success: false, message: 'Select at least one treatment item, or switch back to "Any"' }));
                setTargetingExpanded(true);
                return;
            }
        }
        const detail = {
            practice: practiceId,
            name: compose.name || '',
            subject: compose.subject || '',
            use_unlayer: isUnlayer,
            channel: 'EMAIL',
            category: compose.category || '',
        };
        if (campaign && isUnlayer) {
            detail.unlayer_html = unLayerHtml || '';
            detail.unlayer_template = templateJson || {};
        } else {
            detail.body = modifiedContent;
        }
        if (compose.category === 'COMMUNICATION' && !preventDefaultBaseTemplateAndID) {
            detail.base_template = import.meta.env.VITE_REACT_APP_COMMUNICATION_ID || 3;
        }
        if (compose.category === 'MARKETING' && !preventDefaultBaseTemplateAndID) {
            detail.base_template = import.meta.env.VITE_REACT_APP_MARKETING_ID || 5;
        }
        if (compose.category === 'PROMOTION' && !preventDefaultBaseTemplateAndID) {
            detail.base_template = import.meta.env.VITE_REACT_APP_PROMOTION_ID || 6;
        }

        if (attachmentDirty) {
            detail.attachments = attachmentPayload;
        }
        // Targeting fields — only when feature flag is on and messageType=Template
        if (showTargetingSection) {
            detail.applies_to_statuses = statusMode === 'specific' ? selectedStatuses : [];
            detail.applies_to_treatment_categories = categoryMode === 'specific' ? selectedTreatmentCategories : [];
            detail.applies_to_treatment_items = itemMode === 'specific' ? selectedTreatmentItems : [];
        }
        // Branded Mailgun template wiring — engagement templates only.
        if (showTemplateWiring) {
            detail.use_mailgun_templates = mgWrapped;
            detail.template_id = mgWrapped ? (mgTemplateId || '').trim() : '';
        }
        const attachmentSaveSnapshot = createSaveSnapshot();
        attachmentSaveInFlightRef.current = true;
        setAttachmentSaveInFlight(true);
        handleSaveMessage(detail, composeDetail.id, isSaveAndClose, (result) => {
            attachmentSaveInFlightRef.current = false;
            setAttachmentSaveInFlight(false);
            if (result?.success && result?.data?.attachments) {
                acknowledgeSaved(attachmentSaveSnapshot, result.data.attachments);
            }
        });
    };

    const checkValidationForAutoSave = () => !isEmpty(compose.name) && !isEmpty(compose.subject) && !isEmpty(compose.category);

    useEffect(() => {
        const handleAutoSave = () => {
            if (isAutoSaveOn && !isEmpty(composeDetail) && checkValidationForAutoSave() && campaign && isUnlayer && autoSaveFunction && !attachmentPending && !attachmentBlocked && !attachmentSaveInFlight) {
                setIsAutoSaveOn(false);
                handleSave();
            }
        };
        handleAutoSave();
    }, [autoSaveFunction, attachmentPending, attachmentBlocked, attachmentSaveInFlight]);

    if (openEmailCompose) {
        setTimeout(() => {
            document.body.style.overflow = 'hidden';
        }, 500);
    }
    if (!openEmailCompose) {
        document.body.style.overflowY = 'auto';
        return null;
    }

    const handleTime = (sendTime) => {
        setEditForm(true);
        setCompose({
            ...compose,
            delay: sendTime,
        });
        onCloseContacts();
    };

    // const handleChange = (e, value) => {
    //     if (value) {
    //         const data = `$${value}`;
    //         if (EditorMessage.current) {
    //             const editor = EditorMessage?.current?.getEditor();
    //             editor.insertText(cursor, data, 'link');
    //             editor.setSelection(cursor + data.length);
    //             editor.focus();
    //         }
    //     }
    // };

    const handleChange = (e, val) => {
        // const practiceData = practiceUserData?.data;
        const value = val?.replace(/\s*\([^()]*\)$/, '');
        // const mappings = {
        //     'practice_email_logo': { type: 'image', data: practiceData?.practice_email_logo },
        //     'practice_name': { type: 'text', data: practiceData?.practice_name },
        //     'practice_id': { type: 'text', data: practiceData?.practice_id },
        //     'practice_logo_url': { type: 'text', data: practiceData?.practice_logo_url },
        //     'practice_banner_text': { type: 'text', data: practiceData?.practice_banner_text },
        //     'practice_phone': { type: 'text', data: practiceData?.practice_phone },
        //     'practice_email': { type: 'text', data: practiceData?.practice_email },
        //     'practice_whatsapp': { type: 'text', data: practiceData?.practice_whatsapp },
        //     'practice_website': { type: 'text', data: practiceData?.practice_website },
        //     'practice_address_line_1': { type: 'text', data: practiceData?.practice_address_line_1 },
        //     'practice_address_line_2': { type: 'text', data: practiceData?.practice_address_line_2 },
        //     'practice_city': { type: 'text', data: practiceData?.practice_city },
        //     'practice_county': { type: 'text', data: practiceData?.practice_county },
        //     'practice_postcode': { type: 'text', data: practiceData?.practice_postcode },
        //     'practice_full_address': { type: 'text', data: practiceData?.practice_full_address },
        //     'practice_opening_hour_monday_start_time': { type: 'text', data: practiceData?.practice_opening_hour_monday_start_time },
        //     'practice_opening_hour_monday_end_time': { type: 'text', data: practiceData?.practice_opening_hour_monday_end_time },
        //     'practice_opening_hour_tuesday_start_time': { type: 'text', data: practiceData?.practice_opening_hour_tuesday_start_time },
        //     'practice_opening_hour_tuesday_end_time': { type: 'text', data: practiceData?.practice_opening_hour_tuesday_end_time },
        //     'practice_opening_hour_wednesday_start_time': { type: 'text', data: practiceData?.practice_opening_hour_wednesday_start_time },
        //     'practice_opening_hour_wednesday_end_time': { type: 'text', data: practiceData?.practice_opening_hour_wednesday_end_time },
        //     'practice_opening_hour_thursday_start_time': { type: 'text', data: practiceData?.practice_opening_hour_thursday_start_time },
        //     'practice_opening_hour_thursday_end_time': { type: 'text', data: practiceData?.practice_opening_hour_thursday_end_time },
        //     'practice_opening_hour_friday_start_time': { type: 'text', data: practiceData?.practice_opening_hour_friday_start_time },
        //     'practice_opening_hour_friday_end_time': { type: 'text', data: practiceData?.practice_opening_hour_friday_end_time },
        //     'practice_opening_hour_saturday_start_time': { type: 'text', data: practiceData?.practice_opening_hour_saturday_start_time },
        //     'practice_opening_hour_saturday_end_time': { type: 'text', data: practiceData?.practice_opening_hour_saturday_end_time },
        //     'practice_opening_hour_sunday_start_time': { type: 'text', data: practiceData?.practice_opening_hour_sunday_start_time },
        //     'practice_opening_hour_sunday_end_time': { type: 'text', data: practiceData?.practice_opening_hour_sunday_end_time },
        //     'practice_meta_chatbot_name': { type: 'text', data: practiceData?.practice_meta_chatbot_name },
        //     'practice_meta_privacy_policy_url': { type: 'text', data: practiceData?.practice_meta_privacy_policy_url },
        //     'practice_social_online_booking': { type: 'text', data: practiceData?.practice_social_online_booking },
        // };



        if (value) {
            const editor = EditorMessage?.current?.getEditor();

            // if (mappings[value]) {
            //     const { type, data } = mappings[value];

            //     if (type === 'image') {
            //         editor.insertEmbed(cursor, 'image', data);
            //     } else {
            //         editor.insertText(cursor, data, 'link');
            //     }
            //     editor.setSelection(cursor + data?.length || 0);
            // } else {
            const data = `${value}`;
            editor.insertText(cursor, data, 'link');
            editor.setSelection(cursor + data.length);
            // }

            editor.focus();
        }
    };


    const disabledTemplate = () => {
        if ((messageType === 'Practice_Message' || messageType === 'OnBoarding') && compose.subject.trim() && editForm && !attachmentPending && !attachmentBlocked && !attachmentSaveInFlight) {
            return false;
        }

        if (compose.name.trim() && compose.category.trim() && compose.subject.trim() && !attachmentPending && !attachmentBlocked && !attachmentSaveInFlight && editForm) {
            return false;
        }
        if (messageType === 'LeadTemplate' && screen !== 'consent') {
            if (compose?.subject?.trim() && editForm && !attachmentPending && !attachmentBlocked && !attachmentSaveInFlight) {
                return false;
            }
        }
        return true;
    };

    // Human-readable explanation of what is keeping the Save buttons disabled.
    // Returned as a string so callers can render it inside a <Tooltip> (which
    // renders nothing when the value is empty).
    const getDisabledReason = () => {
        if (!disabledTemplate()) return '';
        const missing = [];
        if (attachmentSaveInFlight) missing.push('current save to finish');
        if (attachmentPending) missing.push('attachments to finish processing');
        if (attachmentBlocked) missing.push('valid readable attachments');
        if (messageType === 'Practice_Message' || messageType === 'OnBoarding') {
            if (!compose?.subject?.trim()) missing.push('Subject');
            if (!editForm) missing.push('changes to save');
        } else if (messageType === 'LeadTemplate' && screen !== 'consent') {
            if (!compose?.subject?.trim()) missing.push('Subject');
            if (!editForm) missing.push('changes to save');
        } else {
            if (!compose?.name?.trim()) missing.push('Template name');
            if (!compose?.category?.trim()) missing.push('Category');
            if (!compose?.subject?.trim()) missing.push('Subject');
            if (!editForm) missing.push('changes to save');
        }
        return missing.length ? `Required: ${missing.join(', ')}` : '';
    };

    const disabledConsentCompose = () => {
        if (compose?.subject?.trim() && (user && Object.values(user)?.length !== 0) && editForm && !attachmentPending && !attachmentBlocked && !attachmentSaveInFlight) {
            return false;
        }
        return true;
    };

    const parseMergeTags = (datalabels) => (
        // Use regex to find all words starting with '$' followed by alphanumeric characters or underscores
        datalabels?.replace(/\$[a-zA-Z0-9_]+/g, (match) => {
            // Remove the '$' sign to get the actual key
            const key = match.slice(1);

            // Return the value from the dataMap if it exists, otherwise return the original field
            return Object.prototype.hasOwnProperty.call(dataMap, key) ? dataMap[key] : match;
        }) ?? datalabels);

    const handleSend = async () => {
        if (attachmentPending || attachmentBlocked) {
            dispatch(updateError({
                success: false,
                message: attachmentValidationError?.message || attachmentError?.message || 'Please wait for attachments to finish processing.',
            }));
            return;
        }
        // Engagement-template sends use the server template's stored attachments.
        // Transactional sends create new attachment rows and therefore need fresh
        // data URLs rather than retained template ids.
        const attachmentSendMode = getEmailAttachmentSendMode({
            usesStoredTemplateAttachments: Boolean(composeDetail?.superAdmin),
        });
        const sendAttachments = attachmentSendMode === 'server' ? undefined : await prepareForSend();
        if (attachmentSendMode === 'copy' && sendAttachments === null) return;
        const detail = {
            name: compose.name || '',
            subject: compose.subject || '',
            use_unlayer: isUnlayer,
            channel: 'EMAIL',
            lead: getValues('lead_id'),
        };
        if (sendAttachments !== undefined) detail.attachments = sendAttachments;
        if (campaign && isUnlayer) {
            detail.unlayer_html = unLayerHtml || '';
            detail.unlayer_template = templateJson || {};
        } else {
            detail.body = editor || body || '';
        }
        const finalDetails = {
            ...detail,
            name: parseMergeTags(detail?.name),
            subject: parseMergeTags(detail?.subject),
            body: parseMergeTags(detail?.body)
        }
        handleSendEmail(finalDetails, user);
    };

    const searchLeadHandler = (value, reason) => {
        if (reason === 'input') {
            setSearchQuery(value);
        }
        if (value.length === 0) {
            dispatch(clearTaskLead());
        }
    };

    const noOptionsHandler = () => {
        if (searchQuery.trim() === '' || taskSearchLead && taskSearchLead.length === 0) {
            return <SearchNotFound searchQuery={searchQuery} type={screen} onClick={handleCreateLead} />;
        }
        return '';
    };

    const handleCreateLead = () => {
        onOpenConsent();
        dispatch(clearLead());
        dispatch(setCurrentTreatmentDataGlobal(null));
    };

    const handleSearchLead = (e, value) => {
        if (value) {
            setValue('leadName', `${value?.first_name} ${value?.last_name}`);
            setSearchQuery(`${value?.first_name} ${value?.last_name}`);
            setUser(value);
            setValue('lead_id', value?.id);
            dispatch(saveConsentLead(value));
        }
        clearErrors('leadName');
        setUser(value !== null ? value : {});
    };

    const handleAttachment = (event, value, perform) => {
        if (perform === 'clear') {
            removeSavedAttachment(value, event);
            setEditForm(true);
            return;
        }
        replaceNewFiles(event);
        setEditForm(true);
    };

    const handleIsUnlayerChange = (event) => {
        if (event.target.checked) {
            isLoaded.current = true;
        }
        setIsUnlayer(event.target.checked);
    };

    const handleImageLibrary = (data, done) => {
        const handleClick = (e) => {
            if (e.target.classList.contains('library-image') && e.target.closest('#mediaLibrary')) {
                if (e.target.getAttribute('src')) {
                    onCloseCustomLib();
                    setInputFiles(null);
                    done({ url: e.target.getAttribute('src') });
                    dispatch(getMediaImages({ practiceId, limit: 50, offset: 0, shouldReset: true }));
                }
                // Remove the event listener after handling the click
                document.removeEventListener('click', handleClick);
            }
        };

        onOpenCustomLib();

        // Attach the event listener
        document.addEventListener('click', handleClick);
    };

    const handleCloseMediaLibrary = () => {
        onCloseCustomLib();
        dispatch(getMediaImages({ practiceId, limit: 50, offset: 0, shouldReset: true, }));
        setInputFiles(null);
        dispatch(setFileUploadError(''));
    };

    const editorOnChange = (value) => {
        handleChangeEvent(value, 'body');
        if (EditorMessage.current) {
            const editor = EditorMessage.current?.getEditor?.();
            const selection = editor?.getSelection();
            if (selection) {
                const cursorPosition = selection.index + selection.length;
                setCursor(cursorPosition);
            }
        }
    };

    const editorOnChangeSelection = (range) => {
        if (range?.index) {
            setCursor(range?.index);
        }
    };

    const handleClosePopup = () => {
        if (disabledTemplate()) {
            handleClose();
            return;
        }
        templateConfirmation.onTrue();
    };

    const getHeaderTitle = () => {
        if (useOrSend === 'use') return compose.name;
        if (useOrSend === 'send') {
            const contact = leadDetail || user;
            return (
                <>
                    <span style={{ textTransform: 'capitalize' }}>
                        {contact?.first_name || contact?.lead?.first_name} {contact?.last_name || contact?.lead?.last_name}
                    </span>
                    <span style={{ ...commaSpanStyle, fontSize: '13px', color: '#637381' }}>
                        {BeforeComa}{contact?.lead_contact?.email || contact?.lead?.lead_contact?.email}{AfterComa}
                    </span>
                </>
            );
        }
        // eslint-disable-next-line no-nested-ternary
        return isEdit ? (composeDetail?.message && `${composeDetail?.message?.lead_status} Email Message`) : 'New Email Message';
    };

    return (
        <Portal>
            <Backdrop open={fullScreen || !isDesktop} sx={backdropStyle} />
            <RootStyle sx={rootStyle(fullScreen, screen)} className='email-message-compose'>
                {imageLoader &&
                    <Box sx={imgLoaderBoxStyle}>
                        <CircularProgress size={56} thickness={4} sx={{ opacity: 0.48 }} />
                    </Box>
                }
                <FormProvider fullScreenForm={fullScreen} methods={methods}>
                    {/* ── Header Bar ── */}
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 2, py: 1.5,
                        borderBottom: `1px solid ${APPLE.grayBorder}`,
                        bgcolor: APPLE.grayBg,
                        minHeight: 56,
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                            {useOrSend && useOrSend === 'use' && (
                                <IconButton onClick={handleClose} sx={{ mr: 0.5 }}>
                                    <Iconify icon={'eva:arrow-ios-back-fill'} width={20} height={20} />
                                </IconButton>
                            )}
                            {(messageType === 'Template') ? (
                                <RHFTextField
                                    name="name"
                                    placeholder="Template name *"
                                    variant="standard"
                                    value={compose.name}
                                    sx={{
                                        flex: 1,
                                        'input': { fontWeight: 600, fontSize: '16px', fontFamily: APPLE.font },
                                        'input::placeholder': { fontWeight: 600, opacity: 0.8 },
                                        // Show a subtle underline when the field is empty so users
                                        // recognise it as an editable input rather than a title.
                                        '& .MuiInputBase-root::before': {
                                            borderBottom: !compose?.name?.trim() ? `1px dashed ${APPLE.grayBorder}` : 'none',
                                        },
                                        '& .MuiInputBase-root:hover::before': {
                                            borderBottom: !compose?.name?.trim() ? `1px solid ${APPLE.accent}` : 'none',
                                        },
                                    }}
                                    onChange={(event) => handleChangeEvent(event.target.value, 'name')}
                                />
                            ) : (
                                <Typography variant="h6" sx={{ ...typographyStyle, fontFamily: APPLE.font, fontSize: '16px' }} noWrap>
                                    {getHeaderTitle()}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                            {(messageType === 'Practice_Message' || messageType === 'OnBoarding') && (
                                <FormControlLabel
                                    control={<Switch checked={compose.is_enabled} onChange={(event) => handleChangeEvent(event.target.checked, 'is_enabled')} />}
                                    label={compose.is_enabled ? 'Published' : 'Unpublished'}
                                    sx={{ mr: 1, '& .MuiTypography-root': { fontSize: '13px', fontFamily: APPLE.font } }}
                                />
                            )}
                            <IconButton onClick={fullScreen ? () => setFullScreen(false) : () => setFullScreen(true)} sx={{ width: 32, height: 32 }}>
                                <Iconify icon={fullScreen ? 'eva:collapse-fill' : 'eva:expand-fill'} width={18} height={18} />
                            </IconButton>
                            <IconButton onClick={handleClosePopup} sx={{ width: 32, height: 32 }}>
                                <Iconify icon={'eva:close-fill'} width={18} height={18} />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* ── Category + Lead Search (conditional) ── */}
                    {(messageType === 'Template' || (useOrSend === 'send' && screen === 'consent')) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, borderBottom: `1px solid ${APPLE.grayBorder}` }}>
                        {messageType === 'Template' && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormControl sx={formControlStyle}>
                                    <Iconify sx={{ color: '#637381', marginRight: '5px' }} icon={'bi:filter'} width={20} height={20} />
                                    <InputLabel id="select-multiple-chip" sx={{ fontWeight: 600 }}>
                                        Add to category
                                    </InputLabel>
                                    <Select
                                        labelId="select-multiple-chip"
                                        id="multiple-chip"
                                        value={compose.category}
                                        onChange={(event) => handleChangeEvent(event.target.value, 'category')}
                                        placeholder="Add to category"
                                        label="Add to category"
                                        input={
                                            <OutlinedInput
                                                placeholder="Add to category"
                                                inputProps={{ opacity: '1' }}
                                                id="select-multiple-chip"
                                                sx={{ opacity: '0' }}
                                                style={{ opacity: '1' }}
                                                label="Add to category"
                                            />
                                        }
                                        sx={multipleChipStyle}
                                    >
                                        {addCategory.map((name, index) => (
                                            <MenuItem
                                                sx={selectMenuItemStyle}
                                                key={index}
                                                value={name}
                                            >
                                                {name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        )}
                        {(useOrSend && useOrSend === 'send' && screen === 'consent') &&
                            <Controller
                                name={'leadName'}
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <Autocomplete
                                        {...field}
                                        autoFocus
                                        name={'leadName'}
                                        inputValue={searchQuery.trim()}
                                        value={searchQuery.trim()}
                                        clearIcon={false}
                                        options={taskSearchLead && taskSearchLead?.length > 0 ? taskSearchLead : []}
                                        filterOptions={(option) => option}
                                        loading={isLoading}
                                        sx={{ width: '400px' }}
                                        onInputChange={(event, value, reason) => searchLeadHandler(value, reason)}
                                        noOptionsText={noOptionsHandler()}
                                        onChange={(e, value) => handleSearchLead(e, value)}
                                        isOptionEqualToValue={(option, value) => option?.first_name === value}
                                        getOptionLabel={(option) => `${option?.first_name} ${option?.last_name}`}
                                        renderOption={(props, option) => {
                                            const { first_name: firstName, last_name: lastName } = option;
                                            return (
                                                <Box component="li" {...props} key={option.id} >
                                                    {firstName || ''} {lastName || ''}
                                                </Box>
                                            );
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                error={!!error}
                                                helperText={(searchQuery?.length === 0 && error) && 'This field is required'}
                                                placeholder="Search lead name or email"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Iconify icon="uil:search" />
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        )}
                                    />
                                )}
                            />
                        }
                    </Box>
                    )}

                    {/* ── Targeting Section — "When should this template be available?" ── */}
                    {showTargetingSection && (
                        <Box sx={{ borderBottom: `1px solid ${APPLE.grayBorder}` }}>
                            {/* Collapsible header row — matching Category row height/padding */}
                            <Box
                                sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => setTargetingExpanded((v) => !v)}
                            >
                                <Iconify icon="mdi:target" width={20} sx={{ color: '#637381', mr: '5px' }} />
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, fontFamily: APPLE.font, color: '#212B36', flex: 1 }}>
                                    When available?
                                </Typography>
                                {!targetingExpanded && (statusMode === 'specific' || categoryMode === 'specific' || itemMode === 'specific') && (
                                    <Typography variant="caption" sx={{ color: APPLE.accent, mr: 1, fontFamily: APPLE.font }}>Targeting set</Typography>
                                )}
                                {!targetingExpanded && statusMode === 'any' && categoryMode === 'any' && itemMode === 'any' && (
                                    <Typography variant="caption" sx={{ color: '#919EAB', mr: 1, fontFamily: APPLE.font }}>Any lead</Typography>
                                )}
                                <Iconify icon={targetingExpanded ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} width={18} sx={{ color: '#919EAB' }} />
                            </Box>
                            <Collapse in={targetingExpanded}>
                                <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {/* Lead Status */}
                                    <Box>
                                        <FormControl component="fieldset" size="small" fullWidth>
                                            <FormLabel sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#637381', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, fontFamily: APPLE.font }}>
                                                Lead Status
                                            </FormLabel>
                                            <RadioGroup row value={statusMode} onChange={(e) => { setStatusMode(e.target.value); if (e.target.value === 'any') setSelectedStatuses([]); }}>
                                                <FormControlLabel value="any" control={<Radio size="small" />} label={<Typography variant="body2" sx={{ fontFamily: APPLE.font }}>Any</Typography>} />
                                                <FormControlLabel value="specific" control={<Radio size="small" />} label={<Typography variant="body2" sx={{ fontFamily: APPLE.font }}>Specific</Typography>} />
                                            </RadioGroup>
                                        </FormControl>
                                        {statusMode === 'specific' && (
                                            <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {TEMPLATE_STATUS_OPTIONS.map((opt) => {
                                                    const active = selectedStatuses.includes(opt.value);
                                                    return (
                                                        <Chip
                                                            key={opt.value}
                                                            label={opt.label}
                                                            size="small"
                                                            variant={active ? 'filled' : 'outlined'}
                                                            onClick={() => {
                                                                const next = active ? selectedStatuses.filter((v) => v !== opt.value) : [...selectedStatuses, opt.value];
                                                                setSelectedStatuses(next);
                                                                if (next.length === 0) setStatusMode('any');
                                                            }}
                                                            sx={{
                                                                cursor: 'pointer', fontSize: '0.7rem', fontWeight: active ? 600 : 400, fontFamily: APPLE.font,
                                                                ...(active ? { bgcolor: APPLE.accent, color: '#fff', borderColor: 'transparent', '&:hover': { bgcolor: alpha(APPLE.accent, 0.85) } } : { borderColor: alpha('#919EAB', 0.24) }),
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        )}
                                    </Box>
                                    {/* Treatment Category */}
                                    <Box>
                                        <FormControl component="fieldset" size="small" fullWidth>
                                            <FormLabel sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#637381', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, fontFamily: APPLE.font }}>
                                                Treatment Category
                                            </FormLabel>
                                            <RadioGroup row value={categoryMode} onChange={(e) => { setCategoryMode(e.target.value); if (e.target.value === 'any') setSelectedTreatmentCategories([]); }}>
                                                <FormControlLabel value="any" control={<Radio size="small" />} label={<Typography variant="body2" sx={{ fontFamily: APPLE.font }}>Any</Typography>} />
                                                <FormControlLabel value="specific" control={<Radio size="small" />} label={<Typography variant="body2" sx={{ fontFamily: APPLE.font }}>Specific</Typography>} />
                                            </RadioGroup>
                                        </FormControl>
                                        {categoryMode === 'specific' && (
                                            <Box sx={{ mt: 0.5, border: `1px solid ${alpha('#919EAB', 0.16)}`, borderRadius: '8px', maxHeight: 140, overflow: 'auto' }}>
                                                {treatmentsData.categories.length === 0 ? (
                                                    <Box sx={{ p: 1.5 }}><Typography variant="caption" sx={{ color: '#919EAB', fontFamily: APPLE.font }}>No categories found</Typography></Box>
                                                ) : (
                                                    <List dense disablePadding>
                                                        {treatmentsData.categories.map((cat) => (
                                                            <ListItemButton key={cat.id} dense sx={{ px: 1, py: 0.5 }} onClick={() => {
                                                                const next = selectedTreatmentCategories.includes(cat.id) ? selectedTreatmentCategories.filter((v) => v !== cat.id) : [...selectedTreatmentCategories, cat.id];
                                                                setSelectedTreatmentCategories(next);
                                                                if (next.length === 0) setCategoryMode('any');
                                                            }}>
                                                                <ListItemIcon sx={{ minWidth: 32 }}>
                                                                    <Checkbox size="small" checked={selectedTreatmentCategories.includes(cat.id)} sx={{ p: 0, color: '#C4CDD5', '&.Mui-checked': { color: APPLE.accent } }} />
                                                                </ListItemIcon>
                                                                <ListItemText primary={cat.name} primaryTypographyProps={{ variant: 'body2', fontFamily: APPLE.font, fontWeight: selectedTreatmentCategories.includes(cat.id) ? 600 : 400 }} />
                                                            </ListItemButton>
                                                        ))}
                                                    </List>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                    {/* Treatment Item */}
                                    <Box>
                                        <FormControl component="fieldset" size="small" fullWidth>
                                            <FormLabel sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#637381', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, fontFamily: APPLE.font }}>
                                                Treatment Item
                                            </FormLabel>
                                            <RadioGroup row value={itemMode} onChange={(e) => { setItemMode(e.target.value); if (e.target.value === 'any') setSelectedTreatmentItems([]); }}>
                                                <FormControlLabel value="any" control={<Radio size="small" />} label={<Typography variant="body2" sx={{ fontFamily: APPLE.font }}>Any</Typography>} />
                                                <FormControlLabel value="specific" control={<Radio size="small" />} label={<Typography variant="body2" sx={{ fontFamily: APPLE.font }}>Specific</Typography>} />
                                            </RadioGroup>
                                        </FormControl>
                                        {itemMode === 'specific' && (
                                            <Box sx={{ mt: 0.5, border: `1px solid ${alpha('#919EAB', 0.16)}`, borderRadius: '8px', maxHeight: 180, overflow: 'auto' }}>
                                                {treatmentsData.items.length === 0 ? (
                                                    <Box sx={{ p: 1.5 }}><Typography variant="caption" sx={{ color: '#919EAB', fontFamily: APPLE.font }}>No items found</Typography></Box>
                                                ) : (
                                                    <List dense disablePadding>
                                                        {treatmentsData.items.map((item) => (
                                                            <ListItemButton key={item.id} dense sx={{ px: 1, py: 0.5 }} onClick={() => {
                                                                const next = selectedTreatmentItems.includes(item.id) ? selectedTreatmentItems.filter((v) => v !== item.id) : [...selectedTreatmentItems, item.id];
                                                                setSelectedTreatmentItems(next);
                                                                if (next.length === 0) setItemMode('any');
                                                            }}>
                                                                <ListItemIcon sx={{ minWidth: 32 }}>
                                                                    <Checkbox size="small" checked={selectedTreatmentItems.includes(item.id)} sx={{ p: 0, color: '#C4CDD5', '&.Mui-checked': { color: APPLE.accent } }} />
                                                                </ListItemIcon>
                                                                <ListItemText primary={item.name} secondary={item.catName} primaryTypographyProps={{ variant: 'body2', fontFamily: APPLE.font, fontWeight: selectedTreatmentItems.includes(item.id) ? 600 : 400 }} secondaryTypographyProps={{ variant: 'caption', fontSize: '0.65rem' }} />
                                                            </ListItemButton>
                                                        ))}
                                                    </List>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Collapse>
                        </Box>
                    )}

                    {showTemplateWiring && (
                        <Box sx={{ borderBottom: `1px solid ${APPLE.grayBorder}` }}>
                            <Box
                                sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => setMgWiringExpanded((v) => !v)}
                            >
                                <Iconify icon="mdi:palette-outline" width={20} sx={{ color: '#637381', mr: '5px' }} />
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, fontFamily: APPLE.font, color: '#212B36', flex: 1 }}>
                                    Branded template
                                </Typography>
                                {!mgWiringExpanded && (
                                    <Typography variant="caption" sx={{ color: mgWrapped ? APPLE.accent : '#919EAB', mr: 1, fontFamily: APPLE.font }}>
                                        {mgWrapped ? (mgTemplateId || 'Mailgun (no id)') : 'Inline body'}
                                    </Typography>
                                )}
                                <Iconify icon={mgWiringExpanded ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'} width={18} sx={{ color: '#919EAB' }} />
                            </Box>
                            <Collapse in={mgWiringExpanded}>
                                <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <FormControl component="fieldset" size="small" fullWidth>
                                        <FormLabel sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#637381', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5, fontFamily: APPLE.font }}>
                                            Rendering
                                        </FormLabel>
                                        <RadioGroup
                                            row
                                            value={mgWrapped ? 'wrapped' : 'inline'}
                                            onChange={(e) => setMgWrapped(e.target.value === 'wrapped')}
                                        >
                                            <FormControlLabel value="inline" control={<Radio size="small" />} label={<Typography variant="body2" sx={{ fontFamily: APPLE.font }}>Inline body</Typography>} />
                                            <FormControlLabel value="wrapped" control={<Radio size="small" />} label={<Typography variant="body2" sx={{ fontFamily: APPLE.font }}>Branded (Mailgun)</Typography>} />
                                        </RadioGroup>
                                    </FormControl>
                                    {mgWrapped && (
                                        <Autocomplete
                                            freeSolo
                                            size="small"
                                            options={mgOptions}
                                            loading={mgLoading}
                                            value={mgTemplateId}
                                            onChange={(_e, val) => setMgTemplateId(val || '')}
                                            onInputChange={(_e, val) => setMgTemplateId(val || '')}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Mailgun template"
                                                    placeholder="e.g. communication"
                                                    helperText={mgOptions.length ? 'Pick from this practice’s Mailgun domain, or type one.' : 'No templates found — type the name if you know it.'}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {mgLoading ? <CircularProgress size={16} /> : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                    )}
                                </Box>
                            </Collapse>
                        </Box>
                    )}

                    {/* ── Subject Row ── */}
                    <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${APPLE.grayBorder}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography sx={{ ...subjectTypoTextStyle, fontFamily: APPLE.font }}>Subject</Typography>
                            <Box sx={subjectInputBoxStyle}>
                                <InputStyle disabled={useOrSend === 'use'} type="text" placeholder="" value={compose.subject} onChange={(event) => handleChangeEvent(event.target.value, 'subject')} sx={subjectInputStyle} />
                            </Box>
                        </Box>
                    </Box>
                    {useOrSend !== 'use' && sortedMergeTags.length > 0 && (
                        <Popover
                            open={Boolean(moreTagsAnchor)}
                            anchorEl={moreTagsAnchor}
                            onClose={() => setMoreTagsAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            PaperProps={{
                                sx: {
                                    width: 320,
                                    maxHeight: 360,
                                    borderRadius: '12px',
                                    boxShadow: APPLE.shadowHover,
                                    mt: 0.5,
                                },
                            }}
                        >
                            <Box sx={{ p: 1.5, borderBottom: `1px solid ${APPLE.grayBorder}` }}>
                                <InputBase
                                    fullWidth
                                    placeholder="Search merge tags..."
                                    value={tagSearch}
                                    onChange={(e) => setTagSearch(e.target.value)}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <Iconify icon="eva:search-fill" width={18} height={18} sx={{ color: APPLE.grayText }} />
                                        </InputAdornment>
                                    }
                                    sx={{
                                        fontFamily: APPLE.font,
                                        fontSize: '13px',
                                        bgcolor: APPLE.grayBg,
                                        borderRadius: APPLE.inputRadius,
                                        px: 1.5,
                                        py: 0.5,
                                    }}
                                />
                            </Box>
                            <Box sx={{ maxHeight: 280, overflowY: 'auto', p: 1 }}>
                                {filteredAllTags.map((tagKey) => {
                                    const isPriority = PRIORITY_TAGS.includes(tagKey);
                                    return (
                                        <Box
                                            key={tagKey}
                                            onClick={() => { handleMergeTagClick(tagKey); setMoreTagsAnchor(null); }}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                px: 1.5,
                                                py: 0.75,
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                '&:hover': { bgcolor: APPLE.accentLight },
                                                transition: 'background 0.15s ease',
                                            }}
                                        >
                                            <Typography sx={{ fontFamily: APPLE.font, fontSize: '13px', fontWeight: isPriority ? 600 : 400, color: 'text.primary' }}>
                                                {formatTagName(tagKey)}
                                            </Typography>
                                            <Typography sx={{ fontFamily: APPLE.font, fontSize: '11px', color: APPLE.grayText, ml: 1, flexShrink: 0 }}>
                                                {formatMergeTagToken(tagKey)}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                                {filteredAllTags.length === 0 && (
                                    <Typography sx={{ fontFamily: APPLE.font, fontSize: '13px', color: APPLE.grayText, textAlign: 'center', py: 2 }}>
                                        No tags found
                                    </Typography>
                                )}
                            </Box>
                        </Popover>
                    )}
                    <Editor
                        id="compose-mail"
                        value={body}
                        simple
                        EditorMessage={EditorMessage}
                        fullScreen={fullScreen}
                        type={type}
                        readOnly={useOrSend === 'use'}
                        ref={textareaRef}
                        onChange={(value) => editorOnChange(value)}
                        onChangeSelection={(range) => editorOnChangeSelection(range)}
                        handleAttachment={composeDetail?.superAdmin && useOrSend === 'send' ? undefined : handleAttachment}
                        file={{ attachments: savedAttachments }}
                        attachmentFiles={attachmentFiles}
                        attachmentError={attachmentError?.message || ''}
                        attachmentPending={attachmentPending}
                        attachmentReadOnly={Boolean(composeDetail?.superAdmin && useOrSend === 'send')}
                        screenType={messageType}
                        bounds={'.app'}
                        placeholder="Type a message"
                        fileSize={attachmentValidationError || {}}
                        campaign={campaign}
                        onLoad={() => {
                            if (isLoaded?.current) {
                                isLoaded.current = false;
                                EditorMessage?.current?.editor?.registerCallback('selectImage', handleImageLibrary);
                                if (!isEmpty(composeDetail?.unlayer_template)) {
                                    EditorMessage?.current?.editor?.loadDesign(composeDetail?.unlayer_template);
                                    EditorMessage?.current?.editor?.setMergeTags(customMergeTags);
                                }
                                if (!isEmpty(templateJson)) {
                                    EditorMessage?.current?.editor?.loadDesign(templateJson);
                                    EditorMessage?.current?.editor?.setMergeTags(customMergeTags);
                                }
                            }
                        }}
                        sx={{
                            borderColor: 'transparent',
                            flexGrow: 1,
                            height: '100%',
                        }}
                        useUnlayer={isUnlayer}
                        handleIsUnlayerChange={handleIsUnlayerChange}
                        practiceId={practiceId}
                        composeDetail={composeDetail}
                        onVariablesClick={useOrSend !== 'use' && sortedMergeTags.length > 0 ? (e) => { setMoreTagsAnchor(e.currentTarget); setTagSearch(''); } : undefined}
                    />
                    <Divider />
                    <Box sx={editorBoxStyle}>
                        {useOrSend && useOrSend !== 'use' && useOrSend !== 'consent' &&
                            <Button onClick={OpenTemplate} sx={{ ...editorBoxBtnStyle, minWidth: 'auto', fontFamily: APPLE.font, textTransform: 'none' }}>
                                <Iconify sx={{ color: 'grey.700', marginRight: '0' }} icon={'material-symbols:calendar-view-month'} width={20} height={20} />
                            </Button>}
                        {!isUnlayer && (useOrSend !== 'use' || useOrSend === undefined) &&
                            <Box sx={{ display: 'flex', width: '30%' }}>
                                <RHFAutocomplete
                                    name={'more'}
                                    size="small"
                                    control={control}
                                    options={getNameList()}
                                    onChange={handleChange}
                                    sx={{ width: '100%' }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Tags"
                                        />
                                    )}
                                />
                            </Box>
                        }
                        {messageType !== 'LeadTemplate' ?
                            <Box sx={{ flex: 1, textAlign: 'right' }}>
                                {(messageType === 'Practice_Message' || messageType === 'OnBoarding') &&
                                    <RHFTextField
                                        name="Send"
                                        label="Send"
                                        readOnly
                                        value={compose.delay && compose.delay.label || ''}
                                        onClick={() => { onOpenContacts(); }}
                                        InputProps={{
                                            startAdornment:
                                                <InputAdornment position="start">
                                                    <Iconify icon={'material-symbols:send'} width={24} height={24} />
                                                </InputAdornment>,
                                        }}
                                        sx={RHFTextFieldStyle}
                                    />}
                                {(messageType === 'Practice_Message' || messageType === 'OnBoarding') &&
                                    <Tooltip title={getDisabledReason()} arrow placement="top">
                                        <span>
                                            <Button
                                                sx={{ ...saveBtnStyle, borderRadius: APPLE.inputRadius, fontFamily: APPLE.font, textTransform: 'none', fontWeight: 600 }}
                                                variant="contained"
                                                onClick={handleSave}
                                                disabled={disabledTemplate()}
                                            >
                                                Save
                                            </Button>
                                        </span>
                                    </Tooltip>
                                }
                                {(messageType !== 'Practice_Message' && messageType !== 'OnBoarding') &&
                                    <>
                                        <Tooltip title={getDisabledReason()} arrow placement="top">
                                            <span style={{ marginRight: '12px' }}>
                                                <Button
                                                    sx={{ ...saveBtnStyle, borderRadius: APPLE.inputRadius, fontFamily: APPLE.font, textTransform: 'none', fontWeight: 600, borderColor: APPLE.accent, color: APPLE.accent, '&:hover': { borderColor: APPLE.accentHover, bgcolor: APPLE.accentLight } }}
                                                    variant="outlined"
                                                    onClick={() => handleSave(false)}
                                                    disabled={disabledTemplate()}
                                                >
                                                    Save Template
                                                </Button>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={getDisabledReason()} arrow placement="top">
                                            <span>
                                                <Button
                                                    sx={{ ...saveBtnStyle, borderRadius: APPLE.inputRadius, fontFamily: APPLE.font, textTransform: 'none', fontWeight: 600 }}
                                                    variant="contained"
                                                    onClick={() => handleSave(true)}
                                                    disabled={disabledTemplate()}
                                                >
                                                    Save & close
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    </>
                                }
                            </Box> :
                            <Box sx={{ flex: 1, textAlign: 'right' }}>
                                {(useOrSend === 'send' || useOrSend === 'consent') &&
                                    <Button
                                        sx={{ ...saveBtnStyle, borderRadius: APPLE.inputRadius, fontFamily: APPLE.font, textTransform: 'none', fontWeight: 600 }}
                                        variant="contained"
                                        onClick={handleSend}
                                        disabled={screen !== 'consent' ? disabledTemplate() : disabledConsentCompose()}
                                    >
                                        Send Email
                                    </Button>
                                }
                                {useOrSend === 'use' &&
                                    <Button
                                        sx={{ ...saveBtnStyle, borderRadius: APPLE.inputRadius, fontFamily: APPLE.font, textTransform: 'none', fontWeight: 600 }}
                                        variant="contained"
                                        onClick={() => {
                                            setTemplate(composeDetail);
                                            setTemplateOpen(false);
                                            if (isFromSearch) {
                                                handleClose();
                                                dispatch(updateShowSubjectList(false));
                                            }
                                        }}
                                    >
                                        Use Template
                                    </Button>
                                }
                            </Box>
                        }
                    </Box>
                </FormProvider>
                <SendMessageDialog
                    open={openContacts}
                    handleTime={handleTime}
                    onClose={onCloseContacts}
                    composeDetail={composeDetail}
                />
                {ConsentBuilder && <OpenDialogPopup
                    open={ConsentBuilder}
                    onClose={onCloseCreateLead}
                    steps={leadStep}
                    type={'createLead'}
                    validStep={validStep}
                    updateStep={(type) => setValidStep(type)}
                    inputRef={inputRef}
                    title='Create Lead'
                    headerButtons
                    setActive={setActive}
                />}
                {templateConfirmation.value &&
                    <Dialog open={templateConfirmation.value}>
                        <DialogTitle sx={{ padding: '40px 40px 0px', textAlign: 'center' }}>
                            The system will not save any data. Are you sure you want to close?
                        </DialogTitle>
                        <DialogActions sx={{ padding: '40px !important', justifyContent: 'center' }}>
                            <Button sx={cancelBtnStyle} onClick={handleClose}>
                                Close Unsaved
                            </Button>
                            <Button type='submit'
                                sx={yesBtnStyle}
                                onClick={() => handleSave(true)}
                            >
                                Save Changes
                            </Button>
                        </DialogActions>
                    </Dialog>
                }
                <CustomMediaLibrary practiceId={practiceId} setInputFiles={setInputFiles} inputFiles={inputFiles} mediaLibraryImage={mediaImages} open={openCustomLib} handleClose={handleCloseMediaLibrary} />
            </RootStyle>
        </Portal >
    );
};

EmailMessageCompose.propTypes = {
    openEmailCompose: PropTypes.bool,
    onCloseEmailCompose: PropTypes.func,
    composeDetail: PropTypes.object,
    isEdit: PropTypes.bool,
    handleSaveMessage: PropTypes.func,
    type: PropTypes.string,
    messageType: PropTypes.string,
    practiceId: PropTypes.string,
    hasTargeting: PropTypes.bool,
    OpenTemplate: PropTypes.bool,
    useOrSend: PropTypes.string,
    setTemplate: PropTypes.func,
    setTemplateOpen: PropTypes.func,
    handleSendEmail: PropTypes.func,
    leadDetail: PropTypes.object,
    sendTemplate: PropTypes.bool,
    screen: PropTypes.string,
    campaign: PropTypes.object,
    isFromSearch: PropTypes.bool,
    preventDefaultBaseTemplateAndID: PropTypes.bool
};

export default EmailMessageCompose;
